/**
 * Turn a stored snapshot back into the shape `CanvasDataContext` consumers
 * expect (task 6.0e).
 *
 * `buildSnapshotCourses` in `snapshotUtils.ts` is the forward transform:
 * (courses, courseDetails) -> snapshot courses. This is its inverse, so offline
 * mode can feed every existing course view from a snapshot without any of them
 * knowing where the data came from. The two must round-trip for every field a
 * snapshot carries; `snapshotAdapter.test.ts` asserts it.
 *
 * ## What is and is not recoverable
 *
 * Task 6.0d widened the stored assignment record precisely so this inverse is
 * faithful. Two things still need care:
 *
 * - `group_name` was added by 6.0d, but snapshots taken *before* it do not have
 *   it. It is reconstructed by joining `group_id` to the snapshot's own
 *   `assignment_groups`, which is exact when the group is present and only
 *   falls back to a placeholder when it genuinely is not.
 * - `description`, `submission_types` and the lock detail fields (`lock_at`,
 *   `unlock_at`, `lock_info`, `lock_explanation`) are not stored at all - see
 *   task 6.0d's decision. Consumers must tolerate their absence; they are left
 *   undefined rather than faked, because a fabricated empty description is
 *   indistinguishable from a real one.
 */

/** One assignment as stored inside a snapshot course. */
export interface SnapshotAssignment {
  id: string | number;
  name: string;
  score?: number | null;
  points_possible?: number | null;
  submitted?: boolean;
  submitted_at?: string | null;
  group_id?: string | number;
  due_at?: string | null;
  missing?: boolean;
  group_name?: string | null;
  late?: boolean;
  excused?: boolean;
  omit?: boolean;
  locked_for_user?: boolean;
  graded_at?: string | null;
}

/** One assignment group as stored inside a snapshot course. */
export interface SnapshotAssignmentGroup {
  id: string | number;
  name?: string | null;
  /** Ungraded work counted as zero — Canvas's *final* score for the group. */
  percentage?: number | null;
  weight?: number;
  group_points?: number;
  submission_points?: number;
  /**
   * Task 2.15. Ungraded work ignored — Canvas's *current* score, and the number
   * its grades page shows. Optional because snapshots taken before 2.15 do not
   * carry it; readers must treat its absence as "unknown", never as zero.
   */
  graded_points?: number;
  graded_percentage?: number | null;
}

/** One course as stored inside a snapshot. */
export interface SnapshotCourse {
  id: string | number;
  name?: string;
  code?: string;
  grade?: number | string | null;
  assignments?: SnapshotAssignment[];
  assignment_groups?: SnapshotAssignmentGroup[];
}

/** A stored snapshot, as returned by GetSnapshots. */
export interface StoredSnapshot {
  id?: string;
  date?: string;
  courses?: SnapshotCourse[];
}

export interface AdaptedCourse {
  /** The key `courseDetails` is indexed by, matching the live fetch path. */
  api_course_id: string;
  id: string;
  name: string;
  course_code: string;
}

export interface AdaptedCourseDetails {
  overall_grade: number | string | null;
  assignments: SnapshotAssignment[];
  assignment_groups: SnapshotAssignmentGroup[];
}

export interface AdaptedSnapshot {
  courses: AdaptedCourse[];
  courseDetails: Record<string, AdaptedCourseDetails>;
}

/**
 * Adapt a stored snapshot into `{ courses, courseDetails }`.
 *
 * Returns empty structures for a missing or malformed snapshot rather than
 * throwing: offline mode's whole purpose is to work when things are broken, so
 * a bad row should render an empty course list, not a blank screen.
 */
export function adaptSnapshot(snapshot: StoredSnapshot | null | undefined): AdaptedSnapshot {
  const snapshotCourses = Array.isArray(snapshot?.courses) ? snapshot!.courses! : [];

  const courses: AdaptedCourse[] = [];
  const courseDetails: Record<string, AdaptedCourseDetails> = {};

  snapshotCourses.forEach((course) => {
    if (!course || course.id === undefined || course.id === null) return;

    const courseId = String(course.id);
    const groups = Array.isArray(course.assignment_groups) ? course.assignment_groups : [];

    // group_id -> name, for reconstructing group_name on snapshots that predate
    // task 6.0d. Ids are stringified on the way in for the same reason the
    // backend stringifies them: a numeric key would not match a string one.
    const groupNames = new Map<string, string>();
    groups.forEach((group) => {
      if (group && group.id !== undefined && group.id !== null) {
        groupNames.set(String(group.id), group.name || 'Uncategorized');
      }
    });

    courses.push({
      api_course_id: courseId,
      id: courseId,
      name: course.name || '',
      course_code: course.code || ''
    });

    courseDetails[courseId] = {
      overall_grade: course.grade ?? null,
      assignments: (Array.isArray(course.assignments) ? course.assignments : []).map(
        (assignment) => adaptAssignment(assignment, groupNames)
      ),
      assignment_groups: groups
    };
  });

  return { courses, courseDetails };
}

function adaptAssignment(
  assignment: SnapshotAssignment,
  groupNames: Map<string, string>
): SnapshotAssignment {
  const groupId = assignment.group_id === undefined || assignment.group_id === null
    ? undefined
    : String(assignment.group_id);

  return {
    ...assignment,
    group_id: groupId,
    // Prefer what was stored (task 6.0d); reconstruct only when absent, which
    // is the case for every snapshot taken before it.
    group_name:
      assignment.group_name ??
      (groupId !== undefined ? groupNames.get(groupId) ?? 'Uncategorized' : 'Uncategorized'),
    // `submitted` is the stored boolean; older snapshots have it without a
    // timestamp. Derive the flag from the timestamp when only that is present
    // so consumers can rely on one of them existing.
    submitted: assignment.submitted ?? Boolean(assignment.submitted_at)
  };
}

/**
 * Pick the snapshot to display: the most recent by date.
 *
 * Offline mode defaults to the newest rather than asking, because the common
 * case is one recent import. The caller can still let the user choose.
 */
export function selectLatestSnapshot<T extends { date?: string }>(
  snapshots: T[] | null | undefined
): T | null {
  if (!Array.isArray(snapshots) || snapshots.length === 0) return null;

  return snapshots.reduce((latest, candidate) => {
    if (!latest?.date) return candidate;
    if (!candidate?.date) return latest;
    return candidate.date > latest.date ? candidate : latest;
  });
}

/**
 * The snapshot nearest in time to `targetIso` (task 6.0s).
 *
 * Used when switching children in offline mode: "show me the other child as
 * of when I was looking at this one". Nearest rather than same-time because
 * two children imported from one 6.0r bundle share a timestamp only if the
 * Import page was given it, and two manual sessions are minutes apart -- an
 * exact match would often find nothing.
 *
 * Undated snapshots lose to dated ones. An unparseable target falls back to
 * the latest, which is what the Offline Mode entry page would have picked.
 * Ties go to the later snapshot.
 */
export function selectNearestSnapshot<T extends { date?: string }>(
  snapshots: T[] | null | undefined,
  targetIso: string | null | undefined
): T | null {
  if (!Array.isArray(snapshots) || snapshots.length === 0) return null;

  const target = targetIso ? new Date(targetIso).getTime() : NaN;
  if (Number.isNaN(target)) return selectLatestSnapshot(snapshots);

  const parse = (snapshot: T): number =>
    snapshot?.date ? new Date(snapshot.date).getTime() : NaN;

  return snapshots.reduce((best, candidate) => {
    const bestTime = parse(best);
    const candidateTime = parse(candidate);
    if (Number.isNaN(candidateTime)) return best;
    if (Number.isNaN(bestTime)) return candidate;

    const bestGap = Math.abs(bestTime - target);
    const candidateGap = Math.abs(candidateTime - target);
    if (candidateGap < bestGap) return candidate;
    if (candidateGap === bestGap && candidateTime > bestTime) return candidate;
    return best;
  });
}

export default { adaptSnapshot, selectLatestSnapshot, selectNearestSnapshot };
